import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore/lite";

import { auth, dbLite } from "../../store/firebaseInit";
import { shouldLogFirestoreDebugTiming } from "../../store/appEnvironment";

const collections = {
  cardClubs: collection(dbLite, "cardClubs"),
  clubPlayers: collection(dbLite, "clubPlayers"),
  gamesData: collection(dbLite, "gamesData"),
  scoreCards: collection(dbLite, "scoreCards"),
  siteMembers: collection(dbLite, "siteMembers"),
};

const inflightRequests = new Map();
const sessionCache = {
  gamesData: null,
};
const logFirestoreDebugTiming = shouldLogFirestoreDebugTiming();

function withDedupe(key, factory) {
  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }

  const promise = Promise.resolve()
    .then(factory)
    .finally(() => {
      inflightRequests.delete(key);
    });

  inflightRequests.set(key, promise);
  return promise;
}

function logOperation(name, result) {
  if (!logFirestoreDebugTiming) {
    return;
  }

  const duration = (result?.endedAt ?? Date.now()) - (result?.startedAt ?? Date.now());
  const logPayload = {
    durationMs: duration,
    error: result?.error?.message ?? null,
    source: result?.source ?? "unknown",
    status: result?.error ? "error" : "ok",
  };

  if (result?.error) {
    console.warn(`[firestore] ${name}`, logPayload);
    return;
  }

  console.info(`[firestore] ${name}`, logPayload);
}

function logDebugStep(name, details) {
  if (!logFirestoreDebugTiming) {
    return;
  }

  console.info(`[firestore:step] ${name}`, details);
}

async function timeStep(name, context, operation) {
  if (!logFirestoreDebugTiming) {
    return operation();
  }

  const startedAt = Date.now();

  try {
    const value = await operation();
    const durationMs = Date.now() - startedAt;
    logDebugStep(name, {
      context,
      durationMs,
      status: "ok",
    });
    return value;
  } catch (error) {
    const durationMs = Date.now() - startedAt;
    logDebugStep(name, {
      context,
      durationMs,
      error: error instanceof Error ? error.message : String(error),
      status: "error",
    });
    throw error;
  }
}

function buildResult(data, source, startedAt, error = null) {
  const endedAt = Date.now();
  return {
    data,
    endedAt,
    error,
    source,
    startedAt,
  };
}

function normalizeError(error, fallbackMessage) {
  if (error instanceof Error) {
    return error;
  }

  return new Error(fallbackMessage ?? "Unknown Firestore error");
}

function getSnapshotSource(snapshot) {
  if (snapshot?.metadata?.fromCache === true) {
    return "cache";
  }
  if (snapshot?.metadata) {
    return "server";
  }
  return "unknown";
}

function mapDocSnapshot(snapshot) {
  return {
    ...snapshot.data(),
    id: snapshot.id,
  };
}

function buildDefaultTransaction(userEmail) {
  const datePurchase = new Date();
  const dateExpires = new Date();
  dateExpires.setDate(dateExpires.getDate() - 1);

  return {
    amountPaid: 0,
    dateExpires,
    datePurchase,
    userEmail,
  };
}

function normalizeSiteMember(siteMemberSnapshot) {
  if (!siteMemberSnapshot?.exists?.()) {
    return {
      exists: false,
      expiresDate: null,
      hasUserEverSubscribed: false,
      isUserSubscriber: false,
      lastScoreCard: null,
      transaction: [buildDefaultTransaction(null)],
    };
  }

  const siteMemberData = siteMemberSnapshot.data();
  const transactionHistory = Array.isArray(siteMemberData.transaction)
    ? siteMemberData.transaction
    : [];
  const latestTransaction =
    transactionHistory.length > 0
      ? transactionHistory[transactionHistory.length - 1]
      : buildDefaultTransaction(siteMemberData.userEmail ?? null);
  const expiresDate =
    latestTransaction?.dateExpires?.toDate instanceof Function
      ? latestTransaction.dateExpires.toDate()
      : latestTransaction?.dateExpires
        ? new Date(latestTransaction.dateExpires)
        : null;

  return {
    exists: true,
    expiresDate,
    hasUserEverSubscribed: transactionHistory.length > 1,
    isUserSubscriber: isDateInFuture(expiresDate),
    lastScoreCard: siteMemberData?.lastScoreCard ?? null,
    transaction: transactionHistory,
  };
}

function isDateInFuture(input) {
  if (!input) {
    return false;
  }

  const inputDate = new Date(input);
  const currentDate = new Date();

  inputDate.setHours(0, 0, 0, 0);
  currentDate.setHours(0, 0, 0, 0);

  return inputDate >= currentDate;
}

function pickSelectedClubId(cardClubs) {
  if (!Array.isArray(cardClubs) || cardClubs.length === 0) {
    return null;
  }

  return cardClubs.find((club) => club.selected)?.id ?? cardClubs[0].id;
}

function pickSelectedGameId(gamesData, cardClubs, selectedCardClubId) {
  if (!Array.isArray(gamesData) || gamesData.length === 0) {
    return "none";
  }

  const selectedClub = cardClubs.find((club) => club.id === selectedCardClubId);
  const preferredGameId = selectedClub?.lastGameSelected;
  if (preferredGameId && gamesData.some((game) => game.id === preferredGameId)) {
    return preferredGameId;
  }

  return gamesData.find((game) => game.selected)?.id ?? gamesData[0].id;
}

async function bootstrapSession() {
  return withDedupe("bootstrapSession", async () => {
    const startedAt = Date.now();

    try {
      const authReadySource = await waitForCurrentUserOrAuthReady();
      const session = {
        claims: {},
        user: auth.currentUser,
      };
      logDebugStep("bootstrapSession:authReady", {
        durationMs: Date.now() - startedAt,
        source: authReadySource,
        userId: session.user?.uid ?? null,
      });

      const result = buildResult(session, "server", startedAt);
      logOperation("bootstrapSession", result);
      return result;
    } catch (error) {
      const result = buildResult(
        null,
        "unknown",
        startedAt,
        normalizeError(error, "Failed to bootstrap session")
      );
      logOperation("bootstrapSession", result);
      return result;
    }
  });
}

async function waitForCurrentUserOrAuthReady() {
  if (auth.currentUser) {
    return "currentUser";
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer = null;

    const finish = (source) => {
      if (settled) {
        return;
      }

      settled = true;
      if (pollTimer) {
        clearInterval(pollTimer);
      }
      resolve(source);
    };

    pollTimer = window.setInterval(() => {
      if (auth.currentUser) {
        finish("currentUser-poll");
      }
    }, 25);

    auth
      .authStateReady()
      .then(() => {
        finish("authStateReady");
      })
      .catch((error) => {
        if (pollTimer) {
          clearInterval(pollTimer);
        }
        reject(error);
      });
  });
}

async function loadSessionClaims(user) {
  const userId = user?.uid ?? "anonymous";

  return withDedupe(`sessionClaims:${userId}`, async () => {
    const startedAt = Date.now();

    try {
      if (!user) {
        const result = buildResult({}, "memory", startedAt);
        logOperation("loadSessionClaims", result);
        return result;
      }

      const tokenResult = await user.getIdTokenResult();
      const result = buildResult(tokenResult?.claims ?? {}, "server", startedAt);
      logOperation("loadSessionClaims", result);
      return result;
    } catch (error) {
      const result = buildResult(
        {},
        "unknown",
        startedAt,
        normalizeError(error, "Failed to load auth claims")
      );
      logOperation("loadSessionClaims", result);
      return result;
    }
  });
}

async function readGamesData() {
  if (sessionCache.gamesData) {
    logDebugStep("readGamesData:cache-hit", {
      count: sessionCache.gamesData.data.length,
    });
    return sessionCache.gamesData;
  }

  const snapshot = await timeStep("readGamesData:getDocs", {}, () =>
    getDocs(collections.gamesData)
  );
  const result = {
    data: snapshot.docs.map(mapDocSnapshot),
    source: "server",
  };
  sessionCache.gamesData = result;
  return result;
}

async function loadHomeContext(userId) {
  return withDedupe(`home:${userId}`, async () => {
    const startedAt = Date.now();

    try {
      const [siteMemberSnapshot, scoreCardsSnapshot, cardClubsSnapshot, gamesDataResult] =
        await Promise.all([
          timeStep("loadHomeContext:siteMember", { userId }, () =>
            getDoc(doc(dbLite, "siteMembers", userId))
          ),
          timeStep("loadHomeContext:scoreCards", { userId }, () =>
            getDocs(
              query(
                collections.scoreCards,
                where("siteMemberID", "==", userId)
              )
            )
          ),
          timeStep("loadHomeContext:cardClubs", { userId }, () =>
            getDocs(
              query(
                collections.cardClubs,
                where("managers", "array-contains", { id: userId })
              )
            )
          ),
          timeStep("loadHomeContext:gamesData", { userId }, () => readGamesData()),
        ]);

      const scoreCards = scoreCardsSnapshot.docs.map((entry) => ({
        id: entry.id,
        name: entry.data().scoreCardName,
      }));

      const cardClubs = cardClubsSnapshot.docs.map(mapDocSnapshot).sort((a, b) => {
        return a.name.localeCompare(b.name);
      });
      const gamesData = [...gamesDataResult.data];
      const selectedCardClubId = pickSelectedClubId(cardClubs);
      const selectedGameId = pickSelectedGameId(
        gamesData,
        cardClubs,
        selectedCardClubId
      );

      const result = buildResult(
        {
          cardClubs,
          gamesData,
          scoreCards,
          selectedCardClubId,
          selectedGameId,
          siteMember: normalizeSiteMember(siteMemberSnapshot),
        },
        "server",
        startedAt
      );
      logOperation("loadHomeContext", result);
      return result;
    } catch (error) {
      const result = buildResult(
        null,
        "unknown",
        startedAt,
        normalizeError(error, "Failed to load home context")
      );
      logOperation("loadHomeContext", result);
      return result;
    }
  });
}

function normalizeGameDataDocument(gameData, fallbackId = null) {
  if (!gameData) {
    return null;
  }

  return {
    id: gameData.id ?? fallbackId,
    ...gameData,
  };
}

function findMatchingLoadedGameData(scoreCard, loadedGamesData = []) {
  if (!Array.isArray(loadedGamesData) || loadedGamesData.length === 0) {
    return null;
  }

  if (scoreCard?.gdID) {
    const exactMatch = loadedGamesData.find((gameData) => gameData.id === scoreCard.gdID);
    if (exactMatch) {
      return exactMatch;
    }
  }

  if (!scoreCard?.vueFileToLoad) {
    return null;
  }

  return (
    loadedGamesData.find(
      (gameData) => gameData.vueFileToLoad === scoreCard.vueFileToLoad
    ) ?? null
  );
}

async function resolveGameDataForScoreCard(scoreCard, loadedGamesData = []) {
  if (!scoreCard?.gdID && !scoreCard?.vueFileToLoad) {
    return { corrected: false, gameData: null };
  }

  let gameData = normalizeGameDataDocument(
    findMatchingLoadedGameData(scoreCard, loadedGamesData)
  );

  if (gameData) {
    logDebugStep("resolveGameDataForScoreCard:loaded-cache-hit", {
      gdID: scoreCard?.gdID ?? null,
      scoreCardId: scoreCard?.id ?? null,
      vueFileToLoad: scoreCard?.vueFileToLoad ?? null,
    });
  }

  if (!gameData && scoreCard?.gdID) {
    const docRef = doc(dbLite, "gamesData", scoreCard.gdID);
    const snapshot = await timeStep(
      "resolveGameDataForScoreCard:getDocByGameId",
      {
        gdID: scoreCard.gdID,
        scoreCardId: scoreCard?.id ?? null,
      },
      () => getDoc(docRef)
    );
    gameData = normalizeGameDataDocument(snapshot.data(), docRef.id);
  }

  const vueFileMatches =
    gameData &&
    scoreCard?.vueFileToLoad &&
    gameData.vueFileToLoad === scoreCard.vueFileToLoad;

  if (vueFileMatches || !scoreCard?.vueFileToLoad) {
    return { corrected: false, gameData };
  }

  const fallbackSnapshot = await timeStep(
    "resolveGameDataForScoreCard:getDocsByVueFile",
    {
      gdID: scoreCard?.gdID ?? null,
      scoreCardId: scoreCard?.id ?? null,
      vueFileToLoad: scoreCard.vueFileToLoad,
    },
    () =>
      getDocs(
        query(
          collections.gamesData,
          where("vueFileToLoad", "==", scoreCard.vueFileToLoad)
        )
      )
  );

  if (fallbackSnapshot.empty) {
    return { corrected: false, gameData };
  }

  const fallbackDoc = fallbackSnapshot.docs[0];
  return {
    corrected: fallbackDoc.id !== scoreCard.gdID,
    gameData: normalizeGameDataDocument(fallbackDoc.data(), fallbackDoc.id),
  };
}

function applyGinRummyDefaults(scoreCard) {
  if (
    scoreCard?.vueFileToLoad !== "GinRummyScoreCard.vue" ||
    scoreCard?.customizable?.gamesPerMatch
  ) {
    return { scoreCard, writePatch: null };
  }

  const gamesPerMatchSetting = {
    note: "genericScoreCard.custNoNote",
    sortOrder: 9,
    title: "ginRummy.gamesPerMatch",
    value: 5,
  };

  return {
    scoreCard: {
      ...scoreCard,
      customizable: {
        ...(scoreCard.customizable ?? {}),
        gamesPerMatch: gamesPerMatchSetting,
      },
    },
    writePatch: {
      "customizable.gamesPerMatch": gamesPerMatchSetting,
    },
  };
}

async function openScorecard(scoreCardId, loadedGamesData = []) {
  return withDedupe(`scorecard:${scoreCardId}`, async () => {
    const startedAt = Date.now();

    try {
      const docRef = doc(dbLite, "scoreCards", scoreCardId);
      const snapshot = await timeStep(
        "openScorecard:getDoc",
        {
          loadedGamesDataCount: Array.isArray(loadedGamesData)
            ? loadedGamesData.length
            : 0,
          scoreCardId,
        },
        () => getDoc(docRef)
      );
      const rawScoreCard = {
        ...snapshot.data(),
        id: docRef.id,
        matches: snapshot.data()?.matches ?? [],
      };
      const ginRummyNormalized = applyGinRummyDefaults(rawScoreCard);

      if (ginRummyNormalized.writePatch) {
        await timeStep(
          "openScorecard:writeGinRummyDefaults",
          { scoreCardId },
          () => updateDoc(docRef, ginRummyNormalized.writePatch)
        );
      }

      const resolvedScoreCard = ginRummyNormalized.scoreCard;
      const { corrected, gameData } = await timeStep(
        "openScorecard:resolveGameData",
        {
          gdID: resolvedScoreCard?.gdID ?? null,
          scoreCardId,
          vueFileToLoad: resolvedScoreCard?.vueFileToLoad ?? null,
        },
        () => resolveGameDataForScoreCard(resolvedScoreCard, loadedGamesData)
      );

      if (corrected && resolvedScoreCard.id && gameData) {
        await timeStep(
          "openScorecard:repairGameLink",
          {
            nextGameId: gameData.id,
            previousGameId: resolvedScoreCard.gdID ?? null,
            scoreCardId,
          },
          () =>
            updateDoc(docRef, {
              gdID: gameData.id,
              gdName: gameData.name,
            })
        );

        resolvedScoreCard.gdID = gameData.id;
        resolvedScoreCard.gdName = gameData.name;
        resolvedScoreCard.vueFileToLoad = gameData.vueFileToLoad;
      }

      const result = buildResult(
        {
          gameData,
          scoreCard: resolvedScoreCard,
        },
        "server",
        startedAt
      );
      logOperation("openScorecard", result);
      return result;
    } catch (error) {
      const result = buildResult(
        null,
        "unknown",
        startedAt,
        normalizeError(error, "Failed to open scorecard")
      );
      logOperation("openScorecard", result);
      return result;
    }
  });
}

async function createScorecardRecord(scorecardPayload) {
  const startedAt = Date.now();

  try {
    const docRef = await addDoc(collections.scoreCards, scorecardPayload);
    const result = buildResult(
      {
        id: docRef.id,
        ...scorecardPayload,
      },
      "server",
      startedAt
    );
    logOperation("createScorecardRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to create scorecard")
    );
    logOperation("createScorecardRecord", result);
    return result;
  }
}

async function createCardClubRecord(cardClubPayload) {
  const startedAt = Date.now();

  try {
    const docRef = await addDoc(collections.cardClubs, cardClubPayload);
    const snapshot = await getDoc(docRef);
    const result = buildResult(mapDocSnapshot(snapshot), "server", startedAt);
    logOperation("createCardClubRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to create card club")
    );
    logOperation("createCardClubRecord", result);
    return result;
  }
}

async function deleteCardClubRecord(cardClubId) {
  const startedAt = Date.now();

  try {
    await deleteDoc(doc(dbLite, "cardClubs", cardClubId));
    const result = buildResult({ cardClubId }, "server", startedAt);
    logOperation("deleteCardClubRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete card club")
    );
    logOperation("deleteCardClubRecord", result);
    return result;
  }
}

async function updateCardClubRecord(cardClubId, updates) {
  const startedAt = Date.now();

  try {
    await updateDoc(doc(dbLite, "cardClubs", cardClubId), updates);
    const result = buildResult({ cardClubId, ...updates }, "server", startedAt);
    logOperation("updateCardClubRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update card club")
    );
    logOperation("updateCardClubRecord", result);
    return result;
  }
}

async function createClubPlayerRecord(playerPayload) {
  const startedAt = Date.now();

  try {
    const docRef = await addDoc(collections.clubPlayers, playerPayload);
    const snapshot = await getDoc(docRef);
    const result = buildResult(mapDocSnapshot(snapshot), "server", startedAt);
    logOperation("createClubPlayerRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to create club player")
    );
    logOperation("createClubPlayerRecord", result);
    return result;
  }
}

async function deleteClubPlayerRecord(clubPlayerId) {
  const startedAt = Date.now();

  try {
    await deleteDoc(doc(dbLite, "clubPlayers", clubPlayerId));
    const result = buildResult({ clubPlayerId }, "server", startedAt);
    logOperation("deleteClubPlayerRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete club player")
    );
    logOperation("deleteClubPlayerRecord", result);
    return result;
  }
}

async function deleteClubPlayersForClub(cardClubId) {
  const startedAt = Date.now();

  try {
      const snapshot = await getDocs(
        query(collections.clubPlayers, where("ccOwner", "==", cardClubId))
      );
    const batch = writeBatch(dbLite);
    snapshot.forEach((clubPlayerDoc) => {
      batch.delete(clubPlayerDoc.ref);
    });
    await batch.commit();

    const result = buildResult(
      { cardClubId, deletedCount: snapshot.size },
      getSnapshotSource(snapshot),
      startedAt
    );
    logOperation("deleteClubPlayersForClub", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete club players for card club")
    );
    logOperation("deleteClubPlayersForClub", result);
    return result;
  }
}

async function updateClubPlayerRecord(clubPlayerId, updates) {
  const startedAt = Date.now();

  try {
    const docRef = doc(dbLite, "clubPlayers", clubPlayerId);
    await updateDoc(docRef, updates);
    const snapshot = await getDoc(docRef);
    const result = buildResult(mapDocSnapshot(snapshot), "server", startedAt);
    logOperation("updateClubPlayerRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update club player")
    );
    logOperation("updateClubPlayerRecord", result);
    return result;
  }
}

async function saveScorecardPatch(scoreCardId, patch) {
  const startedAt = Date.now();

  try {
    await setDoc(doc(dbLite, "scoreCards", scoreCardId), patch, { merge: true });
    const result = buildResult({ scoreCardId, patch }, "server", startedAt);
    logOperation("saveScorecardPatch", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to save scorecard patch")
    );
    logOperation("saveScorecardPatch", result);
    return result;
  }
}

async function updateScorecardRecord(scoreCardId, updates) {
  const startedAt = Date.now();

  try {
    await updateDoc(doc(dbLite, "scoreCards", scoreCardId), updates);
    const result = buildResult({ scoreCardId, ...updates }, "server", startedAt);
    logOperation("updateScorecardRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update scorecard")
    );
    logOperation("updateScorecardRecord", result);
    return result;
  }
}

async function appendScorecardArrayField(scoreCardId, fieldName, value) {
  return saveScorecardPatch(scoreCardId, {
    [fieldName]: arrayUnion(value),
  });
}

async function deleteScorecardRecord(scoreCardId) {
  const startedAt = Date.now();

  try {
    await deleteDoc(doc(dbLite, "scoreCards", scoreCardId));
    const result = buildResult({ scoreCardId }, "server", startedAt);
    logOperation("deleteScorecardRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete scorecard")
    );
    logOperation("deleteScorecardRecord", result);
    return result;
  }
}

async function deleteScorecardsForClub(cardClubId) {
  const startedAt = Date.now();

  try {
    const snapshot = await getDocs(
      query(collections.scoreCards, where("ccID", "==", cardClubId))
    );
    const batch = writeBatch(dbLite);
    snapshot.forEach((scoreCardDoc) => {
      batch.delete(scoreCardDoc.ref);
    });
    await batch.commit();

    const result = buildResult(
      { cardClubId, deletedCount: snapshot.size },
      getSnapshotSource(snapshot),
      startedAt
    );
    logOperation("deleteScorecardsForClub", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete scorecards for card club")
    );
    logOperation("deleteScorecardsForClub", result);
    return result;
  }
}

async function loadClubPlayers(cardClubId) {
  return withDedupe(`clubPlayers:${cardClubId ?? "none"}`, async () => {
    const startedAt = Date.now();

    try {
      if (!cardClubId) {
        const result = buildResult([], "memory", startedAt);
        logOperation("loadClubPlayers", result);
        return result;
      }

      const snapshot = await getDocs(
        query(
          collections.clubPlayers,
          where("ccOwner", "==", cardClubId)
        )
      );
      const result = buildResult(
        snapshot.docs.map(mapDocSnapshot),
        "server",
        startedAt
      );
      logOperation("loadClubPlayers", result);
      return result;
    } catch (error) {
      const result = buildResult(
        null,
        "unknown",
        startedAt,
        normalizeError(error, "Failed to load club players")
      );
      logOperation("loadClubPlayers", result);
      return result;
    }
  });
}

async function writeSiteMemberInfo(userId, payload) {
  const startedAt = Date.now();

  try {
    await setDoc(doc(dbLite, "siteMembers", userId), payload, { merge: true });
    const result = buildResult({ userId, ...payload }, "server", startedAt);
    logOperation("writeSiteMemberInfo", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to write site member info")
    );
    logOperation("writeSiteMemberInfo", result);
    return result;
  }
}

async function updateLastScorecard(userId, lastScoreCard) {
  const startedAt = Date.now();

  try {
    await setDoc(doc(dbLite, "siteMembers", userId), {
      lastScoreCard,
    }, { merge: true });
    const result = buildResult({ lastScoreCard, userId }, "server", startedAt);
    logOperation("updateLastScorecard", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update last scorecard")
    );
    logOperation("updateLastScorecard", result);
    return result;
  }
}

async function appendSiteMemberTransaction(userId, transaction) {
  const startedAt = Date.now();

  try {
    await updateDoc(doc(dbLite, "siteMembers", userId), {
      transaction: arrayUnion(transaction),
    });
    const result = buildResult({ userId, transaction }, "server", startedAt);
    logOperation("appendSiteMemberTransaction", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to append site member transaction")
    );
    logOperation("appendSiteMemberTransaction", result);
    return result;
  }
}

async function ensureSiteMemberSubscriptionAccess({
  userEmail = null,
  userId,
  years = 1,
  days = 0,
}) {
  const startedAt = Date.now();

  try {
    const docRef = doc(dbLite, "siteMembers", userId);
    const siteMemberSnapshot = await getDoc(docRef);
    const datePurchase = new Date();
    const dateExpires = new Date();
    dateExpires.setFullYear(dateExpires.getFullYear() + years);
    dateExpires.setDate(dateExpires.getDate() + days);
    const nextTransaction = {
      amountPaid: 0,
      dateExpires,
      datePurchase,
      ppOrder: { transaction: null },
      userEmail,
    };

    if (!siteMemberSnapshot.exists()) {
      await writeSiteMemberInfo(userId, {
        lastScoreCard: null,
        transaction: [nextTransaction],
      });
      const result = buildResult({ granted: true }, "server", startedAt);
      logOperation("ensureSiteMemberSubscriptionAccess", result);
      return result;
    }

    const siteMember = normalizeSiteMember(siteMemberSnapshot);
    if (siteMember.expiresDate && isDateInFuture(siteMember.expiresDate)) {
      const result = buildResult({ granted: false }, "server", startedAt);
      logOperation("ensureSiteMemberSubscriptionAccess", result);
      return result;
    }

    await appendSiteMemberTransaction(userId, nextTransaction);
    const result = buildResult({ granted: true }, "server", startedAt);
    logOperation("ensureSiteMemberSubscriptionAccess", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to ensure site member subscription access")
    );
    logOperation("ensureSiteMemberSubscriptionAccess", result);
    return result;
  }
}

async function listManagedCardClubIds(userId) {
  const startedAt = Date.now();

  try {
    const snapshot = await getDocs(
      query(collections.cardClubs, where("managers", "array-contains", { id: userId }))
    );
    const result = buildResult(
      snapshot.docs.map((clubDoc) => clubDoc.id),
      getSnapshotSource(snapshot),
      startedAt
    );
    logOperation("listManagedCardClubIds", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to list managed card clubs")
    );
    logOperation("listManagedCardClubIds", result);
    return result;
  }
}

async function deleteSiteMemberRecord(userId) {
  const startedAt = Date.now();

  try {
    await deleteDoc(doc(dbLite, "siteMembers", userId));
    const result = buildResult({ userId }, "server", startedAt);
    logOperation("deleteSiteMemberRecord", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to delete site member record")
    );
    logOperation("deleteSiteMemberRecord", result);
    return result;
  }
}

async function updateSelectedClub(cardClubId) {
  const startedAt = Date.now();

  try {
    const snapshot = await getDocs(query(collections.cardClubs, where("selected", "==", true)));
    await Promise.all(
      snapshot.docs
        .filter((clubDoc) => clubDoc.id !== cardClubId)
        .map((clubDoc) =>
          updateDoc(doc(dbLite, "cardClubs", clubDoc.id), { selected: false })
        )
    );
    await updateDoc(doc(dbLite, "cardClubs", cardClubId), { selected: true });

    const result = buildResult({ cardClubId }, "server", startedAt);
    logOperation("updateSelectedClub", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update selected club")
    );
    logOperation("updateSelectedClub", result);
    return result;
  }
}

async function updateSelectedGame(gameId) {
  const startedAt = Date.now();

  try {
    const snapshot = await getDocs(query(collections.gamesData, where("selected", "==", true)));
    await Promise.all(
      snapshot.docs
        .filter((gameDoc) => gameDoc.id !== gameId)
        .map((gameDoc) =>
          updateDoc(doc(dbLite, "gamesData", gameDoc.id), { selected: false })
        )
    );
    await updateDoc(doc(dbLite, "gamesData", gameId), { selected: true });
    sessionCache.gamesData = null;

    const result = buildResult({ gameId }, "server", startedAt);
    logOperation("updateSelectedGame", result);
    return result;
  } catch (error) {
    const result = buildResult(
      null,
      "unknown",
      startedAt,
      normalizeError(error, "Failed to update selected game")
    );
    logOperation("updateSelectedGame", result);
    return result;
  }
}

function clearFirestoreSessionCache() {
  sessionCache.gamesData = null;
}

export {
  appendScorecardArrayField,
  appendSiteMemberTransaction,
  bootstrapSession,
  clearFirestoreSessionCache,
  createCardClubRecord,
  createClubPlayerRecord,
  createScorecardRecord,
  deleteCardClubRecord,
  deleteClubPlayerRecord,
  deleteClubPlayersForClub,
  deleteScorecardRecord,
  deleteScorecardsForClub,
  deleteSiteMemberRecord,
  ensureSiteMemberSubscriptionAccess,
  listManagedCardClubIds,
  loadSessionClaims,
  loadClubPlayers,
  loadHomeContext,
  openScorecard,
  saveScorecardPatch,
  updateCardClubRecord,
  updateClubPlayerRecord,
  updateLastScorecard,
  updateSelectedClub,
  updateSelectedGame,
  updateScorecardRecord,
  writeSiteMemberInfo,
};
